import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaPlus, FaTable } from "react-icons/fa";
import { useAuth } from "../../../context/AuthContext";
import { useOrganization } from "../../../context/OrganizationContext";
import { usePermissions } from "../../../context/PermissionsContext";
import InlineAlert from "../../UI/DesignStandards/Notifications/InlineAlert";
import {
  Badge,
  PrimaryButton,
  SecondaryButton,
  SkeletonBoard,
} from "../../UI/DesignStandards";
import {
  tableIsOrgDefault,
  useOrgPayoutTables,
} from "./useOrgPayoutTables";

const OrganizationPayoutTables = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { canManageTournaments, canManageOrganization } = usePermissions();
  const canEdit = canManageTournaments || canManageOrganization;
  const { tables, defaultPayoutTableId, loading, error, createTable } =
    useOrgPayoutTables(currentOrganization?.id);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("Payout table");
  const [actionError, setActionError] = useState("");

  const defaultTable = tables.find((table) =>
    tableIsOrgDefault(table, defaultPayoutTableId)
  );

  const handleCreate = async () => {
    if (!user) return;
    setActionError("");
    try {
      const table = await createTable({
        name: newName.trim() || "Payout table",
        userId: user.id,
      });
      setCreating(false);
      setNewName("Payout table");
      navigate(`/organization/payout-tables/${table.id}`);
    } catch (err) {
      console.error(err);
      setActionError("Could not create the table. Try again.");
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6" role="status" aria-label="Loading payout tables">
        <SkeletonBoard rows={2} />
        <SkeletonBoard header={false} rows={5} />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <header className="card-board overflow-hidden">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
              Payout tables
            </h1>
            <p className="text-sm text-brand-mist mt-1">
              {defaultTable
                ? `New tournaments start on ${defaultTable.latestVersion?.name || defaultTable.name}. Changing the default does not move events already created.`
                : "Pick a default so new tournaments get a table. That does not move events already created."}
            </p>
          </div>
          {canEdit && !creating && (
            <PrimaryButton onClick={() => setCreating(true)} icon={FaPlus}>
              Create table
            </PrimaryButton>
          )}
        </div>
      </header>

      {error ? (
        <InlineAlert type="error" title="There was a problem" message={error} />
      ) : null}
      {actionError ? (
        <InlineAlert
          type="error"
          title="There was a problem"
          message={actionError}
          onClose={() => setActionError("")}
        />
      ) : null}

      {creating && canEdit && (
        <div className="card-board p-6">
          <h2 className="heading-display text-lg mb-4">New payout table</h2>
          <label className="block text-sm font-medium text-brand-charcoal mb-1">
            Name
          </label>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="w-full max-w-md px-3 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink mb-4"
          />
          <p className="text-sm text-brand-muted mb-4">
            Next screen is the recommended fill. New events still start on
            the default table until you change it.
          </p>
          <div className="flex gap-2">
            <PrimaryButton onClick={handleCreate}>Create</PrimaryButton>
            <SecondaryButton
              onClick={() => setCreating(false)}
              className="!px-3 !py-2 text-sm"
            >
              Cancel
            </SecondaryButton>
          </div>
        </div>
      )}

      {tables.length === 0 && !creating ? (
        <div className="card-board p-8 text-center">
          <FaTable className="mx-auto h-10 w-10 text-brand-muted mb-3" />
          <h2 className="heading-display text-lg mb-2">No payout tables yet</h2>
          <p className="text-brand-muted mb-4">
            Create one, set the field, then assign it on a tournament.
          </p>
          {canEdit ? (
            <PrimaryButton onClick={() => setCreating(true)} icon={FaPlus}>
              Create table
            </PrimaryButton>
          ) : null}
        </div>
      ) : (
        <div className="card-board overflow-hidden">
          <ul className="divide-y divide-brand-charcoal/10">
            {tables.map((table) => {
              const latest = table.latestVersion || table;
              const isDefault = tableIsOrgDefault(
                table,
                defaultPayoutTableId
              );
              return (
                <li key={table.id}>
                  <Link
                    to={`/organization/payout-tables/${latest.id}`}
                    className="block px-4 py-4 sm:px-6 hover:bg-brand-gold/10"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-brand-charcoal">
                        {latest.name || "Unnamed table"}
                      </span>
                      {isDefault ? (
                        <Badge variant="primary">Default</Badge>
                      ) : null}
                    </div>
                    {latest.description ? (
                      <div className="text-sm text-brand-muted">
                        {latest.description}
                      </div>
                    ) : null}
                    <div className="text-sm text-brand-muted">
                      {isDefault ? "New tournaments start here. " : ""}
                      {table.tournamentCount === 1
                        ? "Used by 1 tournament"
                        : `Used by ${table.tournamentCount} tournaments`}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default OrganizationPayoutTables;
